import { useMemo } from 'react';
import { decode } from 'js-base64';
import { NotificationCard } from '../../components/Card';
import {
  Center,
  Flex,
  Icon,
  Button,
  useDisclosure,
  Box,
} from '@chakra-ui/react';
import { NotificationTime } from '../../components/Time';
import {
  RequisitionDetailsSection,
  RequisitionDetailsList,
} from '../requisition-notifications/components';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { KText } from '@/ui-library/components';
import { HtmlRenderer } from '@/components/ats/HtmlRenderer/HtmlRenderer';
import { hydrateScorecardTable } from '@/components/ats/scorecard-history';
import { SECTION_ID, SECTION_ORDER } from '@/constants/offer-field-constants';

type OfferFieldEntry = {
  section?: string;
  label?: string;
  value?: unknown;
  order?: number;
  variable?: string;
  kind?: string;
};

// Labels are notification-specific — "custom" renders as "Additional details" here (legacy header),
// not "Custom fields". Order comes from shared SECTION_ORDER (BE-mirrored).
const SECTION_LABELS: Record<string, string> = {
  [SECTION_ID.OFFER_DETAILS]: 'Offer details',
  [SECTION_ID.ADDITIONAL_COMPENSATION]: 'Additional compensation',
  [SECTION_ID.CANDIDATE_DETAILS]: 'Candidate details',
  [SECTION_ID.JOB_DETAILS]: 'Job details',
  [SECTION_ID.CUSTOM]: 'Additional details',
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const formatOfferFieldValue = (raw: unknown): string => {
  if (raw === null || raw === undefined) return '';
  if (isPlainObject(raw)) {
    if (!('number' in raw || 'unit' in raw)) return '';
    if (raw.number === null || raw.number === undefined || raw.number === '')
      return '';
    return [raw.number, raw.unit]
      .filter((v) => v !== null && v !== undefined && v !== '')
      .join(' ');
  }
  return String(raw);
};

// RTC parity — match on variable only (no label fallback). Drives FirstSectionForVariable host detection.
const entryIsSalary = (e: { variable?: string }) => e.variable === 'salary';

const SalaryRangeRow = ({ range }: { range?: string | null }) => {
  if (!range) return null;
  return (
    <Flex flexDir="row" gap="8px" minW="0" overflow="hidden">
      <KText size="sm-semi-bold" minW="fit-content" flexShrink={0}>
        Salary range:
      </KText>
      <KText
        size="sm-medium"
        flex="1"
        minW="0"
        isTruncated
        enableTooltip
        tooltipText={range}
      >
        {range}
        {!range.includes('USD') && ' (USD)'}
      </KText>
    </Flex>
  );
};

const SalaryExceedsMaxNote = () => (
  <KText size="sm-medium" color="secondary-700">
    The salary in this offer is higher than the salary range in the requisition
  </KText>
);

const OfferRequestNotificationWrapper = ({
  notification = {},
  onClose = () => {},
  components = {},
}) => {
  const { custom_attributes } = notification || {};
  let data = {};

  try {
    data = JSON.parse(decode(custom_attributes || ''));
  } catch (error) {
    console.error(
      'Error decoding offer request notification attributes:',
      error
    );
  }

  const {
    candidate = {},
    offer = {},
    job = {},
    requisition = {},
    custom_fields = {},
    additional_compensation = {},
    offer_fields,
    candidate_name,
    candidate_email,
    candidate_phone,
    offer_id: offerId,
    offer_job_title,
    requester_name,
    job_name,
    application_candidate_url,
  } = data || {};

  const finalOfferId = offerId || offer?.id;
  // Full absolute URL to the candidate's application view (resume, scorecards
  // etc.) — same destination the approval email/Slack link to. Opened in a new
  // tab so the approver keeps the notification handy to approve/reject.
  const candidateProfileUrl = candidate?.url || application_candidate_url;
  const { OfferApprovalView } = components || {};
  const offerApprovalActions = useDisclosure();

  // Candidate details
  const candidateDetails = [
    { label: 'Name', value: candidate_name || candidate?.name },
    { label: 'Email', value: candidate_email || candidate?.email },
    { label: 'Phone', value: candidate_phone || candidate?.phone },
  ].filter((item) => item.value);

  const offerDetails = [
    { label: 'Offer date', value: offer?.date },
    { label: 'Salary', value: offer?.salary },
    { label: 'Start date', value: offer?.start_date },
    { label: 'Expiration date', value: offer?.expiration_date },
    { label: 'Additional bonus', value: offer?.additional_bonus },
  ].filter((item) => item.value);

  // Additional compensation (equity)
  const equityDetails = [];

  // Additional compensation from additional_compensation object
  if (additional_compensation) {
    if (additional_compensation?.equity) {
      equityDetails.push({
        label: 'Equity',
        value: additional_compensation?.equity,
      });
    }
    if (additional_compensation?.equity_cliff) {
      equityDetails.push({
        label: 'Equity cliff',
        value: additional_compensation.equity_cliff,
      });
    }
    if (additional_compensation?.vesting_period) {
      equityDetails.push({
        label: 'Vesting period',
        value: additional_compensation.vesting_period,
      });
    }
    if (additional_compensation?.equity_strike_price) {
      equityDetails.push({
        label: 'Equity strike price',
        value: additional_compensation.equity_strike_price,
      });
    }
    if (additional_compensation?.post_cliff_vesting_cycle) {
      equityDetails.push({
        label: 'Post cliff vesting cycle',
        value: additional_compensation.post_cliff_vesting_cycle,
      });
    }
  }

  // Job details
  const jobDetails = [
    {
      label: 'Title',
      value: offer_job_title || offer?.job_title || job?.name || job_name,
    },
    { label: 'Reporting manager', value: job?.reporting_manager },
    { label: 'Department', value: job?.department },
    { label: 'Office', value: job?.office },
    { label: 'Req ID', value: job?.req_id },
  ].filter((item) => item.value);

  // Additional details (custom fields)
  // Supports new array format [{label, value, position}] and old map format {label: value}
  const additionalDetails = Array.isArray(custom_fields)
    ? custom_fields
        .filter((cf) => cf.value)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map(({ label, value }) => ({ label, value: String(value) }))
    : Object.entries(custom_fields || {}).map(([key, value]) => ({
        label: key,
        value: String(value),
      }));

  const groupedOfferFields = useMemo(() => {
    const grouped: Record<
      string,
      Array<{ label: string; value: string; variable?: string }>
    > = {};
    if (!Array.isArray(offer_fields)) return grouped;
    [...(offer_fields as OfferFieldEntry[])]
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .forEach((f) => {
        if (!f) return;
        const value = formatOfferFieldValue(f.value);
        if (!value) return;
        const section = String(f.section || '');
        if (!section) return;
        (grouped[section] ||= []).push({
          label: String(f.label || ''),
          value,
          variable: f.variable,
        });
      });
    return grouped;
  }, [offer_fields]);

  // Empty array falls through to legacy render.
  const hasOfferFields = Array.isArray(offer_fields) && offer_fields.length > 0;
  const renderedSectionIds = SECTION_ORDER.filter(
    (id) => groupedOfferFields[id]?.length
  );
  // Salary warning/range hosts in the first section carrying salary (RTC parity).
  const salaryHostSectionId = renderedSectionIds.find((id) =>
    groupedOfferFields[id]?.some(entryIsSalary)
  );

  const handleApproveReject = () => {
    offerApprovalActions?.onOpen();
  };

  return (
    <>
      <Flex alignItems="baseline" gap="6px">
        <Center minW="34px" h="34px" borderRadius="sm" bg="primary-100">
          <Icon as={LayersThree02} color="primary-500" />
        </Center>
        <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
          <KText>
            <KText as="span" fontWeight="semiBold">
              {requester_name}
            </KText>{' '}
            requests offer approval for{' '}
            {candidateProfileUrl ? (
              <KText
                as="span"
                fontWeight="semiBold"
                color="primary-600"
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                data-test-id="notification-candidate-link"
                onClick={() =>
                  window.open(
                    candidateProfileUrl,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                {candidate_name || candidate?.name}
              </KText>
            ) : (
              <KText as="span" fontWeight="semiBold">
                {candidate_name || candidate?.name}
              </KText>
            )}{' '}
            in the{' '}
            <KText as="span" fontWeight="semiBold">
              {offer_job_title || offer?.job_title || job?.name || job_name}
            </KText>{' '}
            job
          </KText>

          {(!hasOfferFields ||
            renderedSectionIds.length > 0 ||
            offer?.approval_request_note) && (
            <RequisitionDetailsSection title="Details">
              <Flex flexDir="column" gap="24px">
                <Flex
                  flexDir="column"
                  gap="16px"
                  p="16px"
                  bg="white"
                  width="100%"
                >
                  {hasOfferFields
                    ? renderedSectionIds.map((sectionId) => {
                        const entries = groupedOfferFields[sectionId];
                        const isSalaryHost = sectionId === salaryHostSectionId;
                        return (
                          <Flex flexDir="column" gap="8px" key={sectionId}>
                            <KText
                              variant="heading_4"
                              color="secondary-900"
                              fontWeight="semiBold"
                            >
                              {SECTION_LABELS[sectionId] || sectionId}
                            </KText>
                            <Box
                              borderBottom="1px solid"
                              borderColor="border-color"
                            />
                            <RequisitionDetailsList data={entries} p="0" />
                            {isSalaryHost &&
                              requisition?.salary_exceeds_max && (
                                <SalaryExceedsMaxNote />
                              )}
                            {isSalaryHost && (
                              <SalaryRangeRow
                                range={requisition?.salary_range}
                              />
                            )}
                          </Flex>
                        );
                      })
                    : null}

                  {!hasOfferFields && candidateDetails.length > 0 && (
                    <Flex flexDir="column" gap="8px">
                      <KText
                        variant="heading_4"
                        color="secondary-900"
                        fontWeight="semiBold"
                      >
                        Candidate details
                      </KText>
                      <Box
                        borderBottom="1px solid"
                        borderColor="border-color"
                      />
                      <RequisitionDetailsList data={candidateDetails} p="0" />
                    </Flex>
                  )}

                  {!hasOfferFields && offerDetails.length > 0 && (
                    <Flex flexDir="column" gap="8px">
                      <KText
                        variant="heading_4"
                        color="secondary-900"
                        fontWeight="semiBold"
                      >
                        Offer details
                      </KText>
                      <Box
                        borderBottom="1px solid"
                        borderColor="border-color"
                      />

                      <RequisitionDetailsList data={offerDetails} p="0" />
                      {requisition?.salary_exceeds_max && (
                        <SalaryExceedsMaxNote />
                      )}
                      <SalaryRangeRow range={requisition?.salary_range} />
                    </Flex>
                  )}

                  {!hasOfferFields && equityDetails.length > 0 && (
                    <Flex flexDir="column" gap="8px">
                      <KText
                        variant="heading_4"
                        color="secondary-900"
                        fontWeight="semiBold"
                      >
                        Additional compensation
                      </KText>
                      <Box
                        borderBottom="1px solid"
                        borderColor="border-color"
                      />

                      <RequisitionDetailsList data={equityDetails} p="0" />
                    </Flex>
                  )}

                  {!hasOfferFields && jobDetails.length > 0 && (
                    <Flex flexDir="column" gap="8px">
                      <KText
                        variant="heading_4"
                        color="secondary-900"
                        fontWeight="semiBold"
                      >
                        Job details
                      </KText>
                      <Box
                        borderBottom="1px solid"
                        borderColor="border-color"
                      />

                      <RequisitionDetailsList data={jobDetails} p="0" />
                    </Flex>
                  )}

                  {!hasOfferFields && additionalDetails.length > 0 && (
                    <Flex flexDir="column" gap="8px">
                      <KText
                        variant="heading_4"
                        color="secondary-900"
                        fontWeight="semiBold"
                      >
                        Additional details
                      </KText>
                      <Box
                        borderBottom="1px solid"
                        borderColor="border-color"
                      />

                      <RequisitionDetailsList data={additionalDetails} p="0" />
                    </Flex>
                  )}

                  {offer?.approval_request_note && (
                    <Flex flexDir="column" gap="8px">
                      <KText
                        variant="heading_4"
                        color="secondary-900"
                        fontWeight="semiBold"
                      >
                        Approval note
                      </KText>
                      <Box
                        borderBottom="1px solid"
                        borderColor="border-color"
                      />

                      <HtmlRenderer
                        htmlString={offer.approval_request_note}
                        transform={hydrateScorecardTable}
                      />
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </RequisitionDetailsSection>
          )}

          <NotificationTime notification={notification} />

          <Flex gap="8px">
            <Button
              variant="primary"
              onClick={handleApproveReject}
              size="sm"
              data-test-id="approve-offer-button"
            >
              Approve
            </Button>
            <Button
              variant="danger"
              onClick={handleApproveReject}
              size="sm"
              data-test-id="reject-offer-button"
            >
              Reject
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {offerApprovalActions?.isOpen && OfferApprovalView && (
        <OfferApprovalView
          modalActions={offerApprovalActions}
          offerId={finalOfferId}
          onSuccess={() => {
            offerApprovalActions?.onClose();
            onClose();
          }}
        />
      )}
    </>
  );
};

export const OfferRequestNotification = ({
  notification = {},
  onClose = () => {},
  components,
}) => {
  return (
    <NotificationCard notification={notification}>
      <OfferRequestNotificationWrapper
        notification={notification}
        onClose={onClose}
        components={components}
      />
    </NotificationCard>
  );
};
